import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

import {
  CloseOutlined,
  LinkOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  Button,
  Drawer,
  Input,
  Modal,
  Radio,
  Select,
  Space,
} from 'antd';
import { RadioChangeEvent } from 'antd/es';
import TextArea from 'antd/es/input/TextArea';
import { useLocation } from 'react-router-dom';

import ConversationUI, { actionsWithN } from './create-mertrics/ConversationUI';
import { deepClone } from '../../../../helpers/utils/deepClone';
import FillingConditions from './create-mertrics/FillingConditions';
import InputWithColorPreview from './create-mertrics/InputWithColorPreview';
import ModalSettings from './create-metrics-modals/ModalSettings';
import Filter from './params/filter';
import { IFilter } from '../../../../app/providers/redux/slices/reports/params/filter/types';
import { getCookie } from '../../../../helpers/utils/cookies';
import {
  filtersDependsOnNamePath,
  filtersMap,
  termsForFilteringCustomMetrics,
} from '../../../../helpers/utils/data';
import { MetricsPaths } from '../../../../helpers/utils/enums';
import {
  filterTree,
  getMetricFormula,
} from '../../../../helpers/utils/functions';
import {
  generateListKeys,
  prepareData,
} from '../../../../helpers/utils/reports';
import {
  showErrorMessage,
  showSuccessMessage,
} from '../../../../helpers/utils/ui';
import { unApi } from '../../api/endpoints/reports/unApi';
import {
  dataItemsJsonUniversal,
  listKeysJsonUniversal,
} from '../../constants/metrics';
import {
  metrics as staticMetricsUniversal,
  groupingsData as staticGroupingsUniversal,
} from '../../constants/reports';
import {
  metrics as staticMetricsAd,
  groupingsData as staticGroupingsAd,
} from '../../constants/reportsAD';
import {
  metrics as staticMetricsOrder,
  groupingsData as staticGroupingsOrder,
} from '../../constants/reportsOrder';
import {
  metrics as staticMetricsPayment,
  groupingsData as staticGroupingsPayment,
} from '../../constants/reportsPayment';
import {
  metrics as staticMetricsWebinar,
  groupingsData as staticGroupingsWebinar,
} from '../../constants/reportsWebinar';
import { useActions } from '../../hooks/useActions';
import { useTypedSelector } from '../../hooks/useTypedSelector';
import Loading from './cohort/components/Loading';
type Props = {
  title: string;
  initialData?: any;
  duplicate?: boolean;
};

const universalMetrics = [ ...staticMetricsUniversal ];
const adMetrics = [ ...staticMetricsAd ];

/**
 * Получает актуальный pathname для определения типа отчёта.
 * Для дашбордов берёт путь из cookie, иначе — из window.location.
 */
const getReportPathname = (): string => {
  return location.pathname.includes('dashboard')
    ? getCookie('dashboardPathname') || window.location.pathname
    : window.location.pathname;
};

export const staticMetrics = {
  [MetricsPaths.WebinarReports]: staticMetricsWebinar,
  [MetricsPaths.Universal]: universalMetrics,
  [MetricsPaths.OrderReports]: staticMetricsOrder,
  [MetricsPaths.PaymentReports]: staticMetricsPayment,
  [MetricsPaths.AdReports]: adMetrics,
};
export const StaticGroupings = {
  [MetricsPaths.WebinarReports]: staticGroupingsWebinar,
  [MetricsPaths.Universal]: staticGroupingsUniversal,
  [MetricsPaths.OrderReports]: staticGroupingsOrder,
  [MetricsPaths.PaymentReports]: staticGroupingsPayment,
  [MetricsPaths.AdReports]: staticGroupingsAd,
};
export enum Units {
  Quantity = 'Количество',
  Currency = 'Валюта',
  Percentage = '%',
}
enum FillingOption {
  Full = 'full',
  ValueBased = 'valueBased',
}

interface ModalState {
  name: string;
  plan: string;
  description: string;
  whatWeCount?: number;
  sum?: number;
  conditionType: string;
  conditionValue: null | number;
  unit: Units | null;
  isFilling: boolean;
}

interface FillingCondition {
  from: string;
  to: string;
  value: string;
  id: number;
}

interface SelectedItem {
  namePath: string[];
}
const CreateMetricsModal: FC<Props> = ({ title, initialData, duplicate }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { getCreatedMetrics, setIsCreateMetricsVisible: setIsVisible } =
    useActions();

  const { isCreateMetricsVisible: visible } = useTypedSelector(
    (state) => state.reports
  );
  const { selectedSchool } = useTypedSelector((state) => state.currentSchool);


 
  const [ isBlocked, setIsBlocked ] = useState(false);
  const [ modalState, setModalState ] = useState<ModalState>({
    name: duplicate ? `${initialData?.name ?? '-'} (копия)` : '',
    plan: initialData?.plan ?? '',
    description: initialData?.description ?? '',
    whatWeCount: undefined,
    sum: undefined,
    conditionType: 'equal',
    conditionValue: null,
    unit: initialData?.unit ?? Units.Quantity,
    isFilling: initialData?.isFilling ?? false,
  });
  const { createdMetrics } = useTypedSelector((state) => state.table);
  const [ fillingConditions, setFillingConditions ] = useState<
    FillingCondition[]
  >(
    initialData?.fillingConditions ?? [
      { from: '', to: '', value: '#000000', id: 1 },
    ]
  );
  const [ converseData, setConverseData ] = useState<any>({
    selectedAction: { id: '', label: '' },
    constant_argument: null,
  });
  const [ isDrawerVisible, setIsDrawerVisible ] = useState(false);
  const [ selected, setSelected ] = useState([]);
  const { filters } = useTypedSelector((state) => state.filter);
  const [ initialFilters, setInitialFilters ] = useState<IFilter[]>([]);
  // Локальное состояние для фильтров метрики, чтобы не трогать глобальные filters
  const [ metricFilters, setMetricFilters ] = useState<IFilter[]>([]);
  // Флаг первого рендера для пропуска сброса фильтров
  const isFirstRenderRef = useRef(true);

  useEffect(() => {
    if (visible) {
      // Сохраняем глобальные фильтры для возможного использования, но НЕ очищаем их
      setInitialFilters(filters);
      // Очищаем локальные фильтры метрики
      setMetricFilters([]);
      // Сбрасываем флаг первого рендера при открытии
      isFirstRenderRef.current = true;
    }
    // При закрытии НЕ восстанавливаем фильтры - это триггерило перезагрузку отчёта
  }, [ visible ]);

  // Сбрасываем фильтры при смене базовой метрики
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    setMetricFilters([]);
  }, [ selected ]);

  const [ isConverse, setIsConverse ] = useState(false);
  const [ backgroundColor, setBackgroundColor ] = useState(
    initialData?.background ?? '#fffffff'
  );
  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setModalState((prevState) => ({ ...prevState, [name]: value }));
  };
  const addCondition = () => {
    if (fillingConditions.length === 10) {
      showErrorMessage('Достигнут лимит по условиям 10 штук.');
      return;
    }
    setFillingConditions((prevConditions) => [
      ...prevConditions,
      { from: '', to: '', value: '#000000', id: prevConditions.length + 1 },
    ]);
  };

  const handleFillingValueChange = (
    index: number,
    field: string,
    newValue: string
  ) => {
    setFillingConditions((prevConditions) => {
      const updatedConditions = [ ...prevConditions ];
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      updatedConditions[index][field] = newValue;
      return updatedConditions;
    });
  };
  
  const handleSubmit = async () => {
    if (selectedSchool?.id && !isBlocked) {
      try {
        setIsBlocked(true);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const baseMetricsName = selected[0]?.dataKey;
        const dataKeysArray = isConverse
          ? Object.values(converseData.inputStates).map(
              (item: any) => item.selected[0]?.dataKey
            )
          : [];
        const metric_formula = isConverse
          ? getMetricFormula(converseData)
          : null;
        if (
          isConverse &&
          dataKeysArray.length < 2 &&
          !actionsWithN.includes(converseData.selectedAction.id)
        ) {
          showErrorMessage('Выберите метрику');
          return;
        }
        if (
          isConverse &&
          converseData.inputStates &&
          !actionsWithN.includes(converseData.selectedAction.id)
        ) {
          for (let key in converseData.inputStates) {
            if (converseData.inputStates[key].selected.length === 0) {
              showErrorMessage('Выберите метрику');
              return;
            }
          }
        }
        if (modalState.name.trim().length === 0) {
          showErrorMessage('Введите название метрики');
          return;
        }
        if (!baseMetricsName && !isConverse) {
          showErrorMessage('Не выбрана базовая метрика');
          return;
        }
        const visual_parameters = JSON.stringify({
          fillingConditions,
          backgroundColor,
          plan: modalState.plan,
          isFilling: modalState.isFilling,
          unit: modalState.unit,
          base_metric_name: isConverse ? null : baseMetricsName,
          metric_formula: isConverse ? metric_formula : null,
        });
        const values = {
          school_id: selectedSchool.id,
          metric_type_id: isConverse ? 2 : 1,
          custom_metric_name: modalState.name,
          description: modalState.description || null,
          base_metric_name: isConverse ? null : baseMetricsName,
          filter_json: isConverse ? null : JSON.stringify(metricFilters),
          visual_parameters: visual_parameters,
          metric_formula: metric_formula,
          constant_argument: isConverse ? converseData.constant_argument : null,
        };
        setIsLoading(true)
        const response = await unApi.addCustomMetric(values, reportPathname);
        if (response.status === 200) {
          showSuccessMessage('Метрика успешно добавлена');
          setTimeout(() => getCreatedMetrics(selectedSchool.id, reportPathname, () => {
            setIsLoading(false);
            // Задержка перед закрытием модального окна, чтобы дать React обработать обновление state
            // и избежать перезагрузки отчёта при изменении isNotParamsFilterVisible
            setTimeout(() => setIsVisible(false), 100);
          }, true), 1500)
        }
      } catch (err) {
        console.error(err);
        showErrorMessage('Произошла ошибка во время добавления метрики.');
      } finally {
        setIsBlocked(false);
      }
    }
  };
  const handleDeleteCondition = (id: number) => {
    setFillingConditions((prevConditions) =>
      prevConditions.filter((condition) => condition.id !== id)
    );
  };

  // Получаем pathname один раз для использования во всех местах
  const reportPathname = getReportPathname();

  // Мемоизируем данные для ConversationUI
  const conversationMetricsData = useMemo(() => prepareData(
    [
      ...staticMetrics[reportPathname as keyof typeof staticMetrics],
      ...deepClone(createdMetrics),
    ],
    '',
    [],
    false
  ), [reportPathname, createdMetrics]);

  // Мемоизируем данные для модального окна выбора метрик
  const dataItemsForModal = useMemo(() => {
    // 1) распарсили
    const rawItems = JSON.parse(
      dataItemsJsonUniversal[reportPathname as keyof typeof dataItemsJsonUniversal]
    );

    // 2) переставили: "Заявки" сразу после "Регистрации"
    const regIndex = rawItems.findIndex((x: any) => x?.name === 'Регистрации');
    const zIndex = rawItems.findIndex((x: any) => x?.name === 'Заявки');

    if (regIndex !== -1 && zIndex !== -1 && zIndex !== regIndex + 1) {
      const [zNode] = rawItems.splice(zIndex, 1);
      rawItems.splice(regIndex + 1, 0, zNode);
    }

    return filterTree(rawItems, termsForFilteringCustomMetrics);
  }, [reportPathname]);

  // Мемоизируем ключи для кастомных метрик
  const listKeysCustomMetric = useMemo(
    () => generateListKeys(deepClone(createdMetrics)),
    [createdMetrics]
  );

  const handleClearCondition = (index: number) => {
    setFillingConditions((currentConditions) =>
      currentConditions.map((condition, i) =>
        i === index
          ? { ...condition, from: '', to: '', value: '#000000' }
          : condition
      )
    );
  };
  const locationState = useLocation();
  
  // Мемоизируем массив фильтров
  const filtersArray = useMemo(() => {
    const selectedTyped = selected as SelectedItem[];
    if (
      selectedTyped?.length > 0 &&
      selectedTyped[0]?.namePath &&
      selectedTyped[0]?.namePath?.length > 0
    ) {
      const namePathKey = selectedTyped[0]
        ?.namePath[0] as keyof typeof filtersDependsOnNamePath['/users/universal'];
      return filtersDependsOnNamePath[reportPathname]?.[namePathKey] || [];
    }
    return filtersMap[reportPathname] || [];
  }, [reportPathname, selected]);

  const handleOpenDrawer = () => {
    setIsDrawerVisible(true);
  };
  const iframeRef = useRef(null);
  const openLink = () => {
    window.open('https://doc.scananalytics.ru/otchety/metriki/#кастомные-метрики--создать-метрику', '_blank');
  };
  const onClose = () => {
    setIsDrawerVisible(false);
  };
  
  // Эффект для настройки стилей drawer и отправки сообщения в iframe
  useEffect(() => {
    if (!isDrawerVisible) return;
    
    const drawerBody = document.querySelector('.ant-drawer-body');
    if (drawerBody) {
      (drawerBody as HTMLElement).style.padding = '0';
    }
    
    const iframe = iframeRef.current as HTMLIFrameElement | null;
    if (iframe?.contentWindow) {
      iframe.contentWindow.postMessage('deleteElement', 'https://lk.scananalytics.ru');
    }
  }, [isDrawerVisible]);
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return (
    <Modal
      style={{ top: 20 }}
      width={'55%'}
      title={
        <span style={{ fontWeight: '700' }}>
          {title}
          <Button
            data-help-id="metric-how-to-create"
            type="primary"
            onClick={handleOpenDrawer}
            style={{
              marginLeft: '10px',
              fontWeight: '600',
              backgroundColor: 'rgb(76, 175, 80)',
              borderColor: 'rgb(76, 175, 80)',
              borderRadius: '30px',
            }}
          >
            Как создать?
          </Button>
        </span>
      }
      onCancel={() => setIsVisible(false)}
      open={visible}
      footer={[
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button data-help-id="metric-save" type={'primary'} onClick={handleSubmit} key="save">
            Сохранить
          </Button>
        </div>,
      ]}
    >
      <Loading loading={isLoading}>
      <div style={{ display: 'flex', columnGap: '20px' }}>
        <div style={{ width: '65%' }}>
          <div style={{ fontWeight: '700', marginBottom: '8px' }}>
            Название метрики
          </div>
          <Input
            name="name"
            value={modalState.name}
            maxLength={100}
            style={{ width: '100%' }}
            onChange={handleInputChange}
          />
        </div>
        <div style={{ width: '35%' }}>
          <div style={{ fontWeight: '700', marginBottom: '8px' }}>Описание</div>
          <TextArea
            maxLength={300}
            style={{ height: '32px', width: '100%' }}
            name="description"
            value={modalState.description}
            onChange={handleInputChange}
          />
        </div>
      </div>
      <div>
        <div style={{ display: 'flex', columnGap: '14px', marginTop: '26px' }}>
          <div>
            <Radio.Group
              value={isConverse}
              onChange={(e) => setIsConverse(e.target.value)}
            >
              <Radio data-help-id="metric-type-filter" value={false}>Добавить фильтр к метрике</Radio>
              <Radio data-help-id="metric-type-formula" value={true}>Рассчитать метрику</Radio>
            </Radio.Group>
          </div>
        </div>
        {!isConverse && (
          <div>
            <ModalSettings
              selectedProps={selected}
              setSelectedProps={setSelected}

              dataItems={dataItemsForModal}
              // dataItems={filterTree(
              //   JSON.parse(
              //     dataItemsJsonUniversal[
              //       (location.pathname.includes('dashboard')
              //         ? getCookie('dashboardPathname')!
              //         : window.location
              //             .pathname) as keyof typeof dataItemsJsonUniversal
              //     ]
              //   ),
              //   termsForFilteringCustomMetrics
              // )}
              listKeys={JSON.parse(
                listKeysJsonUniversal[reportPathname as keyof typeof listKeysJsonUniversal]
              )}
              defSelected={[]}
              defChecked={selected.map((el: { dataKey: string }) => el?.dataKey || '')}
            />
          </div>
        )}
        {isConverse && (
          <ConversationUI
            setConverseData={setConverseData}
            dataItems={conversationMetricsData}
            listKeys={[
              ...JSON.parse(
                listKeysJsonUniversal[reportPathname as keyof typeof listKeysJsonUniversal]
              ),
              ...listKeysCustomMetric,
            ]}
            defSelected={[]}
            defChecked={[]}
          />
        )}
        {!isConverse && (
          <div style={{ marginTop: '24px' }}>
            <Filter
              notParams={true}
              withSegment={false}
              filtersFilter={filtersArray}
              isCompare={false}
              withRefresh={false}
              localFilters={metricFilters}
              setLocalFilters={setMetricFilters}
            />
          </div>
        )}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            columnGap: '40px',
            marginTop: '10px',
          }}
        >
          <div style={{ display: 'flex', columnGap: '40px' }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                rowGap: '8px',
              }}
            >
              <span style={{ fontWeight: '700' }}>Фон столбца:</span>
              <InputWithColorPreview
                value={backgroundColor}
                onChange={setBackgroundColor}
              />
            </div>
          </div>
          <div
            style={{ display: 'flex', flexDirection: 'column', rowGap: '8px' }}
          >
            <span style={{ fontWeight: '700' }}>План</span>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                columnGap: '5px',
              }}
            >
              <Input
                value={modalState.plan}
                onChange={(e) =>
                  setModalState((prev) => ({ ...prev, plan: e.target.value }))
                }
                style={{ width: '86px' }}
              />
              <span>на 1 день</span>
            </div>
          </div>
          <div
            style={{ display: 'flex', flexDirection: 'column', rowGap: '8px' }}
          >
            <span style={{ fontWeight: '700' }}>Единица измерения</span>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                columnGap: '5px',
              }}
            >
              <Select
                defaultValue={modalState.unit}
                placeholder="Выберите единицу измерения"
                onChange={(value: Units) => {
                  setModalState((prevState) => ({
                    ...prevState,
                    unit: value,
                  }));
                }}
                style={{ width: 200 }}
              >
                {Object.entries(Units).map(([ key, value ]) => (
                  <Select.Option key={key} value={key}>
                    {value}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            rowGap: '10px',
            flexDirection: 'column',
            marginTop: '24px',
          }}
        >
          <span style={{ fontWeight: '700' }}>Условия заливки ячейки:</span>
          <Radio.Group
            onChange={(e: RadioChangeEvent) => {
              const fillingOption: FillingOption = e.target
                .value as FillingOption;
              setModalState((prevState) => ({
                ...prevState,
                isFilling: fillingOption === FillingOption.ValueBased,
              }));
            }}
            value={modalState.isFilling ? 'valueBased' : 'full'}
          >
            <Space direction="vertical">
              <Radio value="full">Заливать всю ячейку</Radio>
              <Radio value="valueBased">Заливать ячейку от значения</Radio>
            </Space>
          </Radio.Group>
          {fillingConditions.map((condition, index) => (
            <FillingConditions
              key={condition.id}
              from={condition.from}
              to={condition.to}
              value={condition.value}
              id={condition.id}
              onClear={() => handleClearCondition(index)}
              onDelete={() => handleDeleteCondition(condition.id)}
              onChange={(field, newValue) =>
                handleFillingValueChange(index, field, newValue)
              }
            />
          ))}
        </div>
        <Space style={{ marginTop: '16px' }}>
          <span style={{ fontWeight: '600' }}>Добавить условие заливки</span>
          <Button
            data-help-id="metric-add-condition"
            onClick={addCondition}
            icon={<PlusOutlined />}
            type="primary"
            shape="circle"
          />
        </Space>
      </div>
      <Drawer
        placement="right"
        onClose={onClose}
        visible={isDrawerVisible}
        width={650}
        rootStyle={{ padding: 0, overflow: 'hidden' }}
      >
        <div style={{ position: 'absolute', top: 30, left: -40, zIndex: 1000 }}>
          <Button
            icon={<CloseOutlined />}
            onClick={onClose}
            shape={'circle'}
            style={{ display: 'block', marginBottom: 10 }}
          />
          <Button
            icon={<LinkOutlined />}
            onClick={openLink}
            shape={'circle'}
            style={{ display: 'block' }}
          />
        </div>
        <div style={{ marginTop: '10px' }}></div>
        <iframe
          className="help-iframe"
          ref={iframeRef}
          src={`https://doc.scananalytics.ru/otchety/metriki/?timestamp=${new Date().getTime()}#кастомные-метрики--создать-метрику`}
        />
      </Drawer>
      </Loading>
    </Modal>
  );
};

export default CreateMetricsModal;
